import React, { useMemo, useState } from 'react';
import {
  OutreachContact,
  OutreachContactStatus,
  OutreachMessageDirection,
  OUTREACH_CONTACT_STATUS_LABELS,
  OUTREACH_NOTE_KIND_LABELS,
  OUTREACH_TOPICS,
  outreachSubtopics,
  outreachTopicLabels,
} from '@radevents/shared';
import { C, FONT_BODY, FONT_MONO, avatarOf } from '../admin/inbox';

// Die kleinen Teile, aus denen die Kontaktzeilen des Postfachs bestehen. Sie liegen hier, weil
// sie inzwischen an drei Stellen gebraucht werden: in der Chatliste, im Kopf des Verlaufs und in
// der Kontaktauswahl. Drei Fassungen desselben Chips waeren drei Wahrheiten.

export const STATUS_ORDER: OutreachContactStatus[] = [
  OutreachContactStatus.NEU,
  OutreachContactStatus.KONTAKTIERT,
  OutreachContactStatus.ANTWORT_ERHALTEN,
  OutreachContactStatus.KOOPERATION,
  OutreachContactStatus.KEIN_INTERESSE,
  OutreachContactStatus.BOUNCE,
];


/**
 * Farbe je Status — Fläche für die Pille, `stripe` für den Streifen links an der Listenzeile.
 *
 * Die Beere ist für „Antwort erhalten" reserviert: das ist die Zeile, die eine Handlung verlangt,
 * und dieselbe Farbe trägt in „Anfragen" das Offene. Alles Erledigte (Kooperation) wird grün,
 * alles Abgeschlossene (kein Interesse) grau, Unzustellbares rot.
 */
export const TONE: Record<OutreachContactStatus, { bg: string; fg: string; stripe: string }> = {
  [OutreachContactStatus.NEU]: { bg: '#E6E9F0', fg: '#4A5A75', stripe: '#8FA0BC' },
  [OutreachContactStatus.KONTAKTIERT]: { bg: '#FBF0DC', fg: '#8A6410', stripe: C.warnSignal },
  [OutreachContactStatus.ANTWORT_ERHALTEN]: { bg: C.accentArea, fg: C.accent, stripe: C.accent },
  [OutreachContactStatus.KOOPERATION]: { bg: C.okArea, fg: C.okText, stripe: C.okStripe },
  [OutreachContactStatus.KEIN_INTERESSE]: { bg: '#EFEBEB', fg: '#7C6C72', stripe: '#C9BEC2' },
  [OutreachContactStatus.BOUNCE]: { bg: '#F7E1E1', fg: '#9A3B3B', stripe: '#C98A8A' },
};

/**
 * Hat der Kontakt einen Verlauf? Genau das macht ihn zum Chat. Ein Anruf zählt mit: wer nur
 * telefoniert hat, hatte trotzdem Kontakt.
 */
export const hasHistory = (c: OutreachContact) => (c.messageCount ?? 0) + (c.noteCount ?? 0) > 0;

/**
 * Ein Filter über einer Kontaktliste (Thema, „Meine“ …). Steht über der Chatliste und in der
 * Kontaktauswahl; zwei Fassungen desselben Knopfs sähen nach dem ersten Nachziehen verschieden aus.
 */
export function FilterChip({ label, on, onClick }: { label: string; on: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={on}
      style={{
        fontFamily: FONT_BODY, fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 999,
        cursor: 'pointer', border: `1px solid ${on ? C.accentBorder : C.ruleSoft}`,
        background: on ? C.accentArea : 'transparent', color: on ? C.accent : C.text3,
      }}
    >
      {label}
    </button>
  );
}

export function StatusPill({ status }: { status: OutreachContactStatus }) {
  const t = TONE[status];
  return (
    <span style={{
      fontFamily: FONT_BODY, fontSize: 12, fontWeight: 700, padding: '4px 11px', borderRadius: 999,
      background: t.bg, color: t.fg, whiteSpace: 'nowrap',
    }}>
      {OUTREACH_CONTACT_STATUS_LABELS[status]}
    </span>
  );
}

export function LinkGlyph() {
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2.4" strokeLinecap="round" aria-hidden>
      <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
    </svg>
  );
}

const chip: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 4,
  fontFamily: FONT_MONO, fontSize: 9.5, letterSpacing: '0.09em', padding: '2.5px 7px',
  borderRadius: 5,
};

/**
 * Die Themen eines Kontakts (Werbepartner · Regional, Veranstalter …). Sie ersetzen den Typ-Chip:
 * der fragte fast dasselbe, und zwei Chips für eine Frage liest niemand.
 */
export const topicChips = (c: OutreachContact) => outreachTopicLabels(c.topics).map(label => (
  <span key={label} style={{ ...chip, background: '#E6E9F0', color: '#4A5A75' }}>
    {label.toUpperCase()}
  </span>
));

/**
 * Ein Ein/Aus-Filter, der sich mit dem Thema kombinieren lässt („Nur meine Chats“,
 * „Noch nicht angeschrieben“). Bewusst ein Schalter und keine weitere Pille: in einer Reihe mit
 * den Themen las sich „Meine“ wie ein Thema, und dass beides zusammen geht, sah niemand.
 */
export function FilterSwitch({ label, on, onChange }: { label: string; on: boolean; onChange: (on: boolean) => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      onClick={() => onChange(!on)}
      className="org-vis"
      style={{ fontFamily: FONT_BODY, fontSize: 12, fontWeight: 600, color: on ? C.accent : C.text3 }}
    >
      <span className="org-vis-track" style={{ background: on ? C.accent : C.inputBorder }} aria-hidden>
        <span className="org-vis-knob" style={{ transform: on ? 'translateX(12px)' : 'none' }} />
      </span>
      {label}
    </button>
  );
}

/**
 * Der Themen-Filter über einer Kontaktliste, in zwei Stufen: oben die Themen, und ist eins mit
 * Unterthemen gewählt (Werbepartner), darunter dessen Unterthemen (Regional, Bundesweit). Ein
 * Wert genügt, weil ein Unterthema im Kontakt immer mit seinem Oberthema steht.
 *
 * Angeboten wird nur, was in [contacts] vorkommt. Ein Filter, der nichts findet, ist Lärm.
 */
export function useTopicFilter(contacts: OutreachContact[], initial: string | null = null) {
  const [value, setValue] = useState<string | null>(initial);
  const used = useMemo(() => {
    const keys = new Set<string>();
    contacts.forEach(c => (c.topics ?? []).forEach(t => keys.add(t)));
    return keys;
  }, [contacts]);

  const def = OUTREACH_TOPICS.find(t => t.key === value);
  const root = def?.parent ?? value;
  const roots = OUTREACH_TOPICS.filter(t => !t.parent && used.has(t.key));
  const subs = root ? outreachSubtopics(root).filter(t => used.has(t.key)) : [];

  const rootChips = roots.map(t => (
    <FilterChip key={t.key} label={t.label} on={root === t.key}
      onClick={() => setValue(root === t.key ? null : t.key)} />
  ));
  // Nochmal gedrückt, geht es zurück aufs Oberthema, nicht auf „alle“.
  const subChips = subs.length === 0 ? null : subs.map(t => (
    <FilterChip key={t.key} label={t.label} on={value === t.key}
      onClick={() => setValue(value === t.key ? root : t.key)} />
  ));

  return { value, setValue, rootChips, subChips };
}

/**
 * Hängt der Kontakt an einem Veranstalter aus unserem Verzeichnis — oder steht er nur hier?
 *
 * Der **Typ** beantwortet das nicht: „Veranstalter" ist dort eine freie Auswahl, die jeder von
 * Hand setzen kann. Maßgeblich ist allein `organizerId`, und die setzt nur der Papierflieger an
 * der Veranstalter-Zeile bzw. der Sammelversand. Beide Zustände tragen ein Zeichen: ein
 * fehlender Hinweis liest sich als „steht hier bloß nicht", nicht als Aussage.
 */
export const originChip = (c: OutreachContact) => (
  c.organizerId ? (
    <span
      title={`Gehört zum Veranstalter${c.organization ? ` „${c.organization}"` : ''} in unserem Verzeichnis`}
      style={{ ...chip, background: C.accentArea, color: C.accent }}
    >
      <LinkGlyph />IM VERZEICHNIS
    </span>
  ) : (
    <span
      title="Hängt an keinem Veranstalter bei uns. Von Hand angelegt oder aus einer Anfrage übernommen"
      style={{ ...chip, background: '#EFEBEB', color: '#8A7A80' }}
    >
      FREIER KONTAKT
    </span>
  )
);

/**
 * An diese Adresse geht nichts mehr raus. Der Chip steht überall dort, wo ein Kontakt zur
 * Auswahl steht — sonst schreibt jemand einen Text, den der Server hinterher ablehnt.
 */
export const blockedChip = (reason?: string) => (
  <span
    title={reason ?? 'An diese Adresse wird nichts verschickt.'}
    style={{ ...chip, background: '#F7E1E1', color: '#9A3B3B' }}
  >
    GESPERRT
  </span>
);

/**
 * Was in der Zeile unter dem Namen steht.
 *
 * Bei einem Chat die letzte Nachricht — mit dem Vornamen davor, wenn sie von uns kam („Roman:“,
 * ohne Namen am Konto „Wir:“). Das ist die
 * Auskunft, wegen der man auf eine Chatliste schaut („wer ist dran?"); Organisation und Adresse
 * stehen ohnehin im Kopf des Verlaufs. Ohne Verlauf bleibt die Adresse: dort ist genau sie die
 * Auskunft.
 */
export const chatPreview = (c: OutreachContact): string => {
  const text = (c.lastMessageSnippet ?? '').replace(/\s+/g, ' ').trim();
  if (!text) return c.organization ? `${c.organization} · ${c.email}` : c.email;
  if (c.lastNoteKind) {
    return `${c.lastMessageAuthor ?? 'Wir'} (${OUTREACH_NOTE_KIND_LABELS[c.lastNoteKind]}): ${text}`;
  }
  return c.lastMessageDirection === OutreachMessageDirection.OUT ? `${c.lastMessageAuthor ?? 'Wir'}: ${text}` : text;
};

/** „Roman Antczak" → „RA", „roman" → „RO", „info@verein.de" → „I". */
export function initialsOf(name: string): string {
  const clean = name.trim();
  if (clean.includes('@')) return clean.charAt(0).toUpperCase();
  const parts = clean.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  return clean.slice(0, 2).toUpperCase();
}

/** Der runde Kreis mit den Initialen — dieselbe Farbe wie der Name in der Blase. */
export function Avatar({ name, size = 30 }: { name: string; size?: number }) {
  const tone = avatarOf(name);
  return (
    <span
      aria-hidden
      style={{
        flex: '0 0 auto', width: size, height: size, borderRadius: 999, background: tone.bg, color: tone.fg,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: FONT_BODY, fontWeight: 700, fontSize: Math.round(size * 0.38), letterSpacing: '0.02em',
      }}
    >
      {initialsOf(name)}
    </span>
  );
}

